import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Camera, Upload, Video, X } from "lucide-react"
import { ensureCameraPermission } from "../../utils/devicePermissions.js"
import { isNativeApp } from "../../utils/nativeApp.js"
import { normalizeCameraImage } from "../../utils/normalizeCameraImage.js"
import Alert from "./Alert.js"

const PHOTO_ACCEPT = "image/jpeg,image/jpg,image/png,image/gif,image/webp"
const SHIPMENT_PHOTO_ACCEPT = "image/jpeg,image/jpg,image/png"
const PDF_ACCEPT = "application/pdf"
const VIDEO_ACCEPT = "video/mp4,video/webm,video/quicktime,video/x-m4v,video/3gpp"
const HANDHELD_MQ = "(max-width: 1366px) and (pointer: coarse), (max-width: 1024px)"

const PHOTO_MAX = 1048576
const MEDIA_PHOTO_MAX = 5 * 1024 * 1024
const MEDIA_VIDEO_MAX = 50 * 1024 * 1024
const MAX_PHOTOS = 10
const MAX_VIDEOS = 5

const isVideoFile = (file) =>
  file?.type?.startsWith("video/") || /\.(mp4|webm|mov|m4v|3gp)$/i.test(file?.name || "")

const isPdf = (file) =>
  file?.type === PDF_ACCEPT || /\.pdf$/i.test(file?.name || "")

const isShipmentPhoto = (file) => {
  if (file?.type && SHIPMENT_PHOTO_ACCEPT.split(",").includes(file.type)) return true
  return /\.(jpe?g|png)$/i.test(file?.name || "")
}

const isPhoto = (file) => {
  if (file?.type && PHOTO_ACCEPT.split(",").includes(file.type)) return true
  return /\.(jpe?g|png|gif|webp)$/i.test(file?.name || "")
}

const blobToFile = (blob, name, type) =>
  new File([blob], name, { type: type || blob.type || "image/jpeg", lastModified: Date.now() })

const isUserCancel = (error) =>
  /cancel|dismiss|OS-PLUG-CAMR-0006|OS-PLUG-CAMR-0013/i.test(String(error?.message || error?.code || error || ""))

const mediaResultToFile = async (result, fallbackName) => {
  const path = result?.webPath || result?.uri
  if (!path) return null
  const blob = await (await fetch(path)).blob()
  const format = String(result?.metadata?.format || result?.format || "").toLowerCase()
  const video =
    result?.type === 1 ||
    blob.type.startsWith("video/") ||
    /mp4|mov|webm|m4v|3gp/.test(format)
  const ext = video
    ? (format.includes("mov") ? "mov" : format.includes("webm") ? "webm" : "mp4")
    : (format.includes("png") ? "png" : "jpg")
  const type = blob.type || (video ? "video/mp4" : "image/jpeg")
  const base = String(fallbackName || "capture").replace(/\.[^.]+$/, "")
  return blobToFile(blob, `${base}.${ext}`, type)
}

const useHandheld = () => {
  const [handheld, setHandheld] = useState(() => (
    typeof window !== "undefined" && (isNativeApp() || window.matchMedia(HANDHELD_MQ).matches)
  ))
  useEffect(() => {
    const mq = window.matchMedia(HANDHELD_MQ)
    const sync = () => setHandheld(isNativeApp() || mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])
  return handheld
}

const useHasCamera = (handheld) => {
  const [hasCamera, setHasCamera] = useState(handheld || isNativeApp())
  useEffect(() => {
    if (handheld || isNativeApp()) {
      setHasCamera(true)
      return undefined
    }
    let cancelled = false
    const detect = async () => {
      try {
        if (!navigator.mediaDevices?.enumerateDevices) {
          if (!cancelled) setHasCamera(false)
          return
        }
        const devices = await navigator.mediaDevices.enumerateDevices()
        if (!cancelled) setHasCamera(devices.some((d) => d.kind === "videoinput"))
      } catch {
        if (!cancelled) setHasCamera(false)
      }
    }
    detect()
    return () => {
      cancelled = true
    }
  }, [handheld])
  return hasCamera
}

/**
 * Pick-and-capture. Photo and document uploads show a thumbnail; click it to check the image.
 * mode: "photo" | "document" | "media"
 */
const MediaCaptureField = ({
  mode = "photo",
  disabled = false,
  onPick,
  onClear,
  selectedFile,
  onFiles,
  photoCount = 0,
  videoCount = 0,
  maxPhotos = MAX_PHOTOS,
  maxVideos = MAX_VIDEOS,
  maxDocumentBytes = 5 * 1024 * 1024,
  accept,
}) => {
  const galleryRef = useRef(null)
  const cameraRef = useRef(null)
  const videoRef = useRef(null)
  const liveRef = useRef(null)
  const streamRef = useRef(null)
  const [webcamOpen, setWebcamOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState("")
  const [previewOpen, setPreviewOpen] = useState(false)
  const previewUrlRef = useRef("")
  const handheld = useHandheld()
  const hasCamera = useHasCamera(handheld)
  const isMedia = mode === "media"
  const isDocument = mode === "document"

  const acceptAttr = accept || (isMedia
    ? `${SHIPMENT_PHOTO_ACCEPT},${VIDEO_ACCEPT}`
    : isDocument
      ? `${PHOTO_ACCEPT},${PDF_ACCEPT}`
      : PHOTO_ACCEPT)

  const report = (msg) => {
    if (isMedia) {
      window.dispatchEvent(new CustomEvent("shipment-media-error", { detail: msg }))
    }
    Alert.error(msg)
  }

  const prepareOne = async (raw, { fromCamera = false } = {}) => {
    if (!raw) return null
    if (isMedia && isVideoFile(raw)) return raw
    if (accept && !isPhoto(raw) && !isPdf(raw) && !(isMedia && isVideoFile(raw))) {
      const name = String(raw.name || "").toLowerCase()
      const ok = accept.split(",").some((token) => {
        const t = token.trim().toLowerCase()
        if (t.startsWith(".")) return name.endsWith(t)
        return raw.type === t || t === "image/*"
      })
      if (!ok) {
        report("Invalid file type.")
        return null
      }
      if (raw.size > maxDocumentBytes) {
        report("File is too large.")
        return null
      }
      return raw
    }
    if (isDocument && isPdf(raw)) {
      if (raw.size > maxDocumentBytes) {
        report("File is too large.")
        return null
      }
      return raw
    }
    if (isMedia && !isShipmentPhoto(raw)) {
      report("Shipment media allows only JPG, JPEG, PNG, and video.")
      return null
    }
    if (!isMedia && !isPhoto(raw)) {
      report(isDocument
        ? "Invalid file type. Only JPG, PNG, GIF, WebP, and PDF are allowed."
        : "Invalid file type. Only JPG, PNG, GIF, and WebP are allowed.")
      return null
    }
    let next = raw
    const photoCompress = !isMedia && (fromCamera || next.size > 900000)
    const mediaCompress = isMedia && fromCamera
    if (photoCompress || mediaCompress) {
      try {
        next = await normalizeCameraImage(next, isMedia
          ? { maxDimension: 2400, quality: 0.85 }
          : { maxDimension: 1200, quality: 0.72 })
      } catch {
        // keep original
      }
    }
    if (mode === "photo" && next.size > PHOTO_MAX) {
      report("Photo is still over 1MB after compression. Try again closer to the subject.")
      return null
    }
    if (isMedia && next.size > MEDIA_PHOTO_MAX) {
      report(`Photo too large (max 5MB): ${next.name}`)
      return null
    }
    if (isDocument && next.size > maxDocumentBytes) {
      report("File is too large.")
      return null
    }
    return next
  }

  const emit = async (list, options) => {
    const prepared = []
    let photos = photoCount
    let videos = videoCount
    for (const raw of list) {
      if (isMedia && isVideoFile(raw)) {
        if (raw.size > MEDIA_VIDEO_MAX) {
          report(`Video too large (max 50MB): ${raw.name}`)
          continue
        }
        if (videos >= maxVideos) {
          report(`Maximum ${maxVideos} videos allowed`)
          continue
        }
        videos += 1
        prepared.push(raw)
        continue
      }
      const file = await prepareOne(raw, options)
      if (!file) continue
      if (isMedia) {
        if (photos >= maxPhotos) {
          report(`Maximum ${maxPhotos} photos allowed`)
          continue
        }
        photos += 1
      }
      prepared.push(file)
      if (!isMedia) break
    }
    if (!prepared.length) return
    if (isMedia) onFiles?.(prepared)
    else {
      replacePreview(prepared[0])
      onPick?.(prepared[0])
    }
  }

  const openGallery = async () => {
    if (isMedia && isNativeApp()) {
      try {
        const cameraMod = await import("@capacitor/camera")
        const { Camera, MediaTypeSelection } = cameraMod
        if (typeof Camera.chooseFromGallery === "function") {
          const { results } = await Camera.chooseFromGallery({
            mediaType: MediaTypeSelection?.All ?? 2,
            allowMultipleSelection: true,
            limit: maxPhotos + maxVideos,
          })
          const filesFromGallery = []
          for (const [idx, item] of (results || []).entries()) {
            const file = await mediaResultToFile(item, `shipment-media-${Date.now()}-${idx}`)
            if (file) filesFromGallery.push(file)
          }
          if (filesFromGallery.length) await emit(filesFromGallery, { fromCamera: false })
          return
        }
      } catch (error) {
        if (isUserCancel(error)) return
      }
    }
    galleryRef.current?.click()
  }

  const stopWebcam = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    setWebcamOpen(false)
  }

  const replacePreview = (file) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    if (!file || isPdf(file) || isVideoFile(file) || !isPhoto(file)) {
      previewUrlRef.current = ""
      setPreviewUrl("")
      setPreviewOpen(false)
      return
    }
    const url = URL.createObjectURL(file)
    previewUrlRef.current = url
    setPreviewUrl(url)
  }

  const clearPreview = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    previewUrlRef.current = ""
    setPreviewUrl("")
    setPreviewOpen(false)
  }

  useEffect(() => {
    if (selectedFile === null) clearPreview()
  }, [selectedFile])

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
  }, [])

  useEffect(() => {
    if (webcamOpen && liveRef.current && streamRef.current) {
      liveRef.current.srcObject = streamRef.current
    }
  }, [webcamOpen])

  const openDesktopWebcam = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      report("This browser cannot open the camera.")
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      })
      streamRef.current = stream
      setWebcamOpen(true)
    } catch {
      report("Camera permission denied. Allow camera access in the browser and try again.")
    }
  }

  const snapWebcam = async () => {
    const video = liveRef.current
    if (!video?.videoWidth) return
    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext("2d").drawImage(video, 0, 0)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85))
    stopWebcam()
    if (!blob) return
    const file = blobToFile(blob, `capture-${Date.now()}.jpg`, "image/jpeg")
    await emit([file], { fromCamera: true })
  }

  const openCamera = async () => {
    const allowed = await ensureCameraPermission()
    if (!allowed) {
      report("Camera permission denied. Enable camera access in app settings.")
      return
    }
    if (isNativeApp()) {
      try {
        const cameraMod = await import("@capacitor/camera")
        const { Camera, CameraResultType, CameraSource } = cameraMod
        let photo
        if (typeof Camera.takePhoto === "function") {
          photo = await Camera.takePhoto({ quality: isMedia ? 80 : 70 })
        } else {
          photo = await Camera.getPhoto({
            quality: isMedia ? 80 : 70,
            resultType: CameraResultType.Uri,
            source: CameraSource.Camera,
            correctOrientation: true,
          })
        }
        const file = await mediaResultToFile(photo, isMedia ? `shipment-photo-${Date.now()}` : "capture.jpg")
        if (file) await emit([file], { fromCamera: true })
        return
      } catch (error) {
        if (isUserCancel(error)) return
      }
    }
    const touchCamera = window.matchMedia("(pointer: coarse)").matches
    if (!touchCamera) {
      await openDesktopWebcam()
      return
    }
    cameraRef.current?.click()
  }

  const openVideo = async () => {
    const allowed = await ensureCameraPermission()
    if (!allowed) {
      report("Camera permission denied. Enable camera access in app settings.")
      return
    }
    if (isNativeApp()) {
      try {
        const { Camera } = await import("@capacitor/camera")
        if (typeof Camera.recordVideo === "function") {
          const video = await Camera.recordVideo({ saveToGallery: false, isPersistent: true })
          const file = await mediaResultToFile(video, `shipment-video-${Date.now()}`)
          if (file) {
            await emit([file], { fromCamera: true })
            return
          }
        }
      } catch (error) {
        if (isUserCancel(error)) return
      }
    }
    videoRef.current?.click()
  }

  const onInput = (e, options) => {
    const picked = Array.from(e.target.files || [])
    e.target.value = ""
    if (picked.length) emit(picked, options)
  }

  const actionBtnClass =
    "inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-xl border text-xs font-medium disabled:opacity-50"

  return (
    <div className="min-w-0">
      <input
        ref={galleryRef}
        type="file"
        accept={acceptAttr}
        multiple={isMedia}
        className="hidden"
        disabled={disabled}
        onChange={(e) => onInput(e, { fromCamera: false })}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        disabled={disabled}
        onChange={(e) => onInput(e, { fromCamera: true })}
      />
      {isMedia ? (
        <input
          ref={videoRef}
          type="file"
          accept="video/*"
          capture="environment"
          className="hidden"
          disabled={disabled}
          onChange={(e) => onInput(e, { fromCamera: true })}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        {hasCamera ? (
          <button
            type="button"
            disabled={disabled}
            onClick={openCamera}
            className={`${actionBtnClass} border-blue-300 text-blue-800 bg-blue-50 hover:bg-blue-100`}
          >
            <Camera className="w-3.5 h-3.5" />
            Take photo
          </button>
        ) : null}
        {isMedia && handheld ? (
          <button
            type="button"
            disabled={disabled}
            onClick={openVideo}
            className={`${actionBtnClass} border-violet-300 text-violet-800 bg-violet-50 hover:bg-violet-100`}
          >
            <Video className="w-3.5 h-3.5" />
            Record video
          </button>
        ) : null}
        <button
          type="button"
          disabled={disabled}
          onClick={openGallery}
          className={`${actionBtnClass} border-gray-300 text-gray-800 bg-white hover:bg-gray-50`}
        >
          <Upload className="w-3.5 h-3.5" />
          Upload
        </button>
      </div>
      {previewUrl && !isMedia ? (
        <div className="relative mt-2 inline-block">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="block rounded-lg border border-gray-200 bg-gray-50 p-1 hover:border-blue-300"
            title="View image"
            aria-label="View selected image"
          >
            <img src={previewUrl} alt="Selected upload" className="h-16 w-16 rounded-md object-cover" />
          </button>
          {onClear ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                clearPreview()
                onClear()
              }}
              className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white hover:bg-red-600"
              aria-label="Remove image"
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      ) : null}
      {previewOpen && previewUrl ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-3 shadow-lg"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Image preview"
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">Preview</p>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
                aria-label="Close preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <img src={previewUrl} alt="Selected upload" className="max-h-80 w-full rounded-lg bg-gray-50 object-contain" />
          </div>
        </div>,
        document.body,
      ) : null}
      {webcamOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-3 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-800">Take photo</p>
              <button type="button" onClick={stopWebcam} className="p-1 rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close camera">
                <X className="w-4 h-4" />
              </button>
            </div>
            <video ref={liveRef} autoPlay playsInline muted className="w-full rounded-lg bg-black aspect-video object-cover" />
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={stopWebcam} className={`${actionBtnClass} border-gray-300 text-gray-700 bg-white`}>
                Cancel
              </button>
              <button type="button" onClick={snapWebcam} className={`${actionBtnClass} border-blue-300 text-blue-800 bg-blue-50`}>
                <Camera className="w-3.5 h-3.5" />
                Capture
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default MediaCaptureField
